import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Video, Mic, MicOff, Camera, CameraOff, 
  Play, Square, RotateCcw, Send, Loader2, CheckCircle,
  AlertCircle, TrendingUp, Star, Clock, ChevronRight, Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mülakat soruları
const interviewQuestions = [
  {
    id: 1,
    category: 'Tanışma',
    question: 'Kendinizi kısaca tanıtır mısınız?',
    tips: ['2 dakikayı geçmeyin', 'Gülümsemeyi unutmayın', 'Kameraya bakın'],
    timeLimit: 120
  },
  {
    id: 2,
    category: 'Motivasyon',
    question: 'Neden kabin memuru olmak istiyorsunuz?',
    tips: ['Samimi olun', 'Somut örnekler verin', 'Tutkunuzu gösterin'],
    timeLimit: 120
  },
  {
    id: 3,
    category: 'Deneyim',
    question: 'Zor bir müşteriyle nasıl başa çıktığınızı anlatır mısınız?',
    tips: ['STAR metodunu kullanın', 'Olumlu sonuçla bitirin', 'Sakin kalın'],
    timeLimit: 180
  },
  {
    id: 4,
    category: 'Takım Çalışması',
    question: 'Ekip içinde çatışma yaşadığınız bir durumu anlatın.',
    tips: ['Çözüm odaklı olun', 'Empati gösterin', 'Öğrendiklerinizi paylaşın'],
    timeLimit: 180
  },
  {
    id: 5,
    category: 'Stres Yönetimi',
    question: 'Baskı altında nasıl performans gösterirsiniz?',
    tips: ['Somut örnekler', 'Stratejilerinizi paylaşın', 'Özgüvenli olun'],
    timeLimit: 150
  },
  {
    id: 6,
    category: 'Esneklik',
    question: 'Vardiyalı çalışma ve seyahat hakkında ne düşünüyorsunuz?',
    tips: ['Olumlu yaklaşın', 'Esnekliğinizi gösterin', 'Heyecanınızı belirtin'],
    timeLimit: 120
  },
  {
    id: 7,
    category: 'Senaryo',
    question: 'Uçuş sırasında bir yolcu fenalaşırsa ne yaparsınız?',
    tips: ['Adım adım anlatın', 'Güvenlik önce', 'Sakin kalın'],
    timeLimit: 180
  },
  {
    id: 8,
    category: 'Kapanış',
    question: 'Bize sormak istediğiniz bir soru var mı?',
    tips: ['Şirket hakkında soru sorun', 'İlginizi gösterin', 'Kısa tutun'],
    timeLimit: 90
  }
];

export default function VideoInterview() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [recordingTime, setRecordingTime] = useState(0);
  const [userVideos, setUserVideos] = useState([]);
  const [remainingCredits, setRemainingCredits] = useState(10);
  const [showHistory, setShowHistory] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const recognitionRef = useRef(null);
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Erişim kontrolü - admin için her zaman açık
  const isUnlocked = user?.role === 'admin' || user?.video_interview_unlocked === true;

  useEffect(() => {
    fetchUserVideos();
    return () => {
      stopCamera();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const fetchUserVideos = async () => {
    try {
      const response = await axios.get(`${API}/video-interview/my-videos`, { withCredentials: true });
      setUserVideos(response.data.videos || []);
      setRemainingCredits(response.data.remaining_credits || 10);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    setCameraReady(false);
    
    // Tarayıcı desteğini kontrol et
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError({
        type: 'unsupported',
        message: 'Tarayıcınız kamera erişimini desteklemiyor. Lütfen Chrome, Firefox veya Safari kullanın.'
      });
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' },
        audio: true
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraReady(true);
      setCameraError(null);
    } catch (error) {
      console.error('Camera error:', error);
      
      let errorInfo = {
        type: 'unknown',
        message: 'Kamera erişimi sağlanamadı.'
      };
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorInfo = {
          type: 'permission',
          message: 'Kamera ve mikrofon izni reddedildi.',
          instructions: [
            '1. Tarayıcınızın adres çubuğundaki kilit/kamera ikonuna tıklayın',
            '2. Kamera ve Mikrofon izinlerini "İzin Ver" olarak değiştirin',
            '3. Sayfayı yenileyin ve tekrar deneyin'
          ]
        };
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorInfo = {
          type: 'notfound',
          message: 'Kamera veya mikrofon bulunamadı.',
          instructions: [
            '1. Cihazınızda kamera ve mikrofon olduğundan emin olun',
            '2. Başka bir uygulama kamerayı kullanıyorsa kapatın',
            '3. Kameranızın fiziksel olarak kapalı olmadığını kontrol edin'
          ]
        };
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorInfo = {
          type: 'inuse',
          message: 'Kamera veya mikrofon başka bir uygulama tarafından kullanılıyor.',
          instructions: [
            '1. Zoom, Skype, Teams gibi uygulamaları kapatın',
            '2. Diğer tarayıcı sekmelerini kontrol edin',
            '3. Sayfayı yenileyin ve tekrar deneyin'
          ]
        };
      } else if (error.name === 'OverconstrainedError') {
        errorInfo = {
          type: 'constraints',
          message: 'Kamera ayarları desteklenmiyor.',
          instructions: [
            '1. Farklı bir kamera deneyin',
            '2. Sayfayı yenileyin'
          ]
        };
      }
      
      setCameraError(errorInfo);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Video kayıt - mobil uyumlu
  const startRecording = () => {
    if (!streamRef.current) {
      toast.error('Kamera açık değil. Lütfen kamerayı açın.');
      return;
    }
    
    if (remainingCredits <= 0) {
      toast.error('Analiz hakkınız kalmadı');
      return;
    }
    
    // Speech Recognition başlat (transcript için)
    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.lang = 'tr-TR';
        recognition.continuous = false;
        recognition.interimResults = false;
        
        recognition.onresult = (event) => {
          if (event.results[0]?.isFinal) {
            const text = event.results[0][0].transcript.trim();
            if (text) {
              setTranscript(prev => prev ? prev + ' ' + text : text);
            }
          }
        };
        
        recognition.onend = () => {
          if (isRecording && recognitionRef.current) {
            try { recognitionRef.current.start(); } catch(e) {}
          }
        };
        
        recognition.onerror = (e) => console.log('Speech error:', e.error);
        recognitionRef.current = recognition;
        recognition.start();
        setIsTranscribing(true);
      } catch(e) { 
        console.error('Speech recognition error', e); 
      }
    }
    
    setTranscript('');
    chunksRef.current = [];
    
    // Android öncelikli mimeType - MP4 daha uyumlu
    let mimeType = 'video/mp4';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = '';
        }
      }
    }
    
    console.log('Using mimeType:', mimeType);
    
    try {
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(streamRef.current, options);
      
      mediaRecorder.ondataavailable = (e) => {
        console.log('Data available:', e.data?.size);
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        console.log('Recording stopped, chunks:', chunksRef.current.length);
        
        // Speech recognition durdur
        if (recognitionRef.current) {
          try { recognitionRef.current.stop(); } catch(e) {}
          recognitionRef.current = null;
          setIsTranscribing(false);
        }
        
        // Kamerayı durdur - video oynatmak için
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
        setCameraReady(false);
        
        // Video blob oluştur
        if (chunksRef.current.length > 0) {
          const finalMimeType = mimeType || 'video/webm';
          const blob = new Blob(chunksRef.current, { type: finalMimeType });
          console.log('Blob created:', blob.size, blob.type);
          
          const url = URL.createObjectURL(blob);
          console.log('URL created:', url);
          
          setRecordedBlob(blob);
          setRecordedUrl(url);
          toast.success('Video kaydedildi! Oynatmak için tıklayın.');
        } else {
          toast.error('Video kaydedilemedi. Lütfen tekrar deneyin.');
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(500); // Her 500ms data al - daha güvenilir
      setIsRecording(true);
      setRecordingTime(0);
      
      toast.info('Kayıt başladı...');
      
      // 2 dakika (120 saniye) limit
      const maxTime = 120;
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= maxTime - 1) {
            stopRecording();
            toast.info('Maksimum kayıt süresi (2 dk) doldu');
            return maxTime;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('MediaRecorder error:', error);
      toast.error('Video kaydı başlatılamadı: ' + error.message);
    }
  };

  const stopRecording = () => {
    console.log('Stopping recording...');
    setIsRecording(false);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(e) {}
    }
  };

  const resetRecording = () => {
    // URL temizle
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setAnalysisResult(null);
    setRecordingTime(0);
    setTranscript('');
    chunksRef.current = [];
    
    // Kamerayı tekrar aç
    startCamera();
  };

  // Videoyu profile kaydet
  const saveToProfile = async () => {
    if (!recordedBlob) {
      toast.error('Kaydedilecek video yok');
      return;
    }
    
    setIsAnalyzing(true);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(recordedBlob);
      
      reader.onloadend = async () => {
        const base64Video = reader.result.split(',')[1];
        
        await axios.post(`${API}/video-interview/save`, {
          video_base64: base64Video,
          question_id: selectedQuestion.id,
          question_text: selectedQuestion.question,
          transcript: transcript
        }, { withCredentials: true });
        
        toast.success('Video profilinize kaydedildi!');
        fetchUserVideos();
        setIsAnalyzing(false);
      };
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Video kaydedilemedi');
      setIsAnalyzing(false);
    }
  };

  const analyzeVideo = async () => {
    if (!recordedBlob) {
      toast.error('Önce bir video kaydedin');
      return;
    }
    
    if (remainingCredits <= 0) {
      toast.error('Analiz hakkınız kalmadı');
      return;
    }
    
    setIsAnalyzing(true);
    
    try {
      // Transcript varsa hızlı analiz
      if (transcript && transcript.length > 20) {
        const response = await axios.post(`${API}/ai-interview/analyze-video-transcript`, {
          transcript: transcript,
          question: selectedQuestion.question,
          language: 'tr'
        }, { withCredentials: true });
        
        if (response.data) {
          setAnalysisResult({
            overall_score: response.data.score || 70,
            content_score: Math.round((response.data.score || 70) / 10),
            confidence_score: 7,
            voice_score: 7,
            suggestions: response.data.improvements || [],
            strengths: response.data.strengths || [],
            transcript: transcript
          });
          setRemainingCredits(prev => prev - 1);
          toast.success('Analiz tamamlandı!');
          setIsAnalyzing(false);
          return;
        }
      }
      
      // Video analizi
      const reader = new FileReader();
      reader.readAsDataURL(recordedBlob);
      
      reader.onloadend = async () => {
        try {
          const base64Video = reader.result.split(',')[1];
          
          const response = await axios.post(`${API}/video-interview/analyze`, {
            video_base64: base64Video,
            question_id: selectedQuestion.id,
            question_text: selectedQuestion.question,
            transcript: transcript
          }, { 
            withCredentials: true,
            timeout: 60000
          });
          
          setAnalysisResult(response.data);
          setRemainingCredits(prev => prev - 1);
          toast.success('Video analizi tamamlandı!');
          fetchUserVideos();
        } catch (error) {
          console.error('Video analysis error:', error);
          toast.error(error.response?.data?.detail || 'Video analizi başarısız');
        } finally {
          setIsAnalyzing(false);
        }
      };
      
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Analiz başarısız');
      setIsAnalyzing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const selectQuestion = (question) => {
    setSelectedQuestion(question);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setAnalysisResult(null);
    setRecordingTime(0);
    startCamera();
  };

  // Erişim kilitli ekranı
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-white">Video Mülakat Koçu</h1>
                <p className="text-amber-100 text-sm">AI destekli mülakat pratiği</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-md mx-auto px-4 py-12">
          <Card className="border-0 shadow-xl text-center">
            <CardContent className="p-8">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-3">Erişim Kilitli</h2>
              <p className="text-gray-600 mb-6">
                Video Mülakat Koçu özelliği henüz sizin için aktif edilmemiş. 
                Bu özelliğe erişim sağlamak için yöneticinizle iletişime geçin.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                Ana Menüye Dön
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Soru seçim ekranı
  if (!selectedQuestion) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-white">Video Mülakat Koçu</h1>
                <p className="text-amber-100 text-sm">AI destekli mülakat pratiği</p>
              </div>
              <div className="bg-white/20 px-3 py-1 rounded-full">
                <span className="text-white text-sm font-medium">{remainingCredits} Hak</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Nasıl Çalışır */}
          <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50">
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-800 mb-3">🎯 Nasıl Çalışır?</h3>
              <div className="grid grid-cols-4 gap-3 text-center text-sm">
                <div>
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-lg">1️⃣</span>
                  </div>
                  <p className="text-gray-600">Soru Seç</p>
                </div>
                <div>
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-lg">2️⃣</span>
                  </div>
                  <p className="text-gray-600">Kaydet</p>
                </div>
                <div>
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-lg">3️⃣</span>
                  </div>
                  <p className="text-gray-600">AI Analiz</p>
                </div>
                <div>
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-lg">4️⃣</span>
                  </div>
                  <p className="text-gray-600">Geliştir</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Geçmiş Videolar */}
          {userVideos.length > 0 && (
            <Card className="mb-6 border-0 shadow-md">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">📹 Son Videolarım</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/profile')}>
                    Tümünü Gör
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {userVideos.slice(0, 3).map((video, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Video className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{video.question_text?.substring(0, 40)}...</p>
                          <p className="text-xs text-gray-500">{video.created_at?.substring(0, 10)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-amber-600">{video.score}/100</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Soru Listesi */}
          <h2 className="font-bold text-lg mb-4">📝 Mülakat Soruları</h2>
          <div className="space-y-3">
            {interviewQuestions.map((q) => (
              <Card 
                key={q.id}
                className="border-0 shadow-md cursor-pointer hover:shadow-lg transition-all"
                onClick={() => selectQuestion(q)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold">
                      {q.id}
                    </div>
                    <div className="flex-1">
                      <span className="text-xs text-amber-600 font-medium">{q.category}</span>
                      <p className="font-medium text-gray-800">{q.question}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        Max {Math.floor(q.timeLimit / 60)}:{(q.timeLimit % 60).toString().padStart(2, '0')}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Kayıt ve analiz ekranı
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              stopCamera();
              setSelectedQuestion(null);
            }}
            className="text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          <div className="text-white text-sm">
            {remainingCredits} Analiz Hakkı
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Soru */}
        <Card className="mb-4 border-0 bg-gradient-to-r from-amber-500 to-orange-600">
          <CardContent className="p-4 text-white">
            <span className="text-xs opacity-80">{selectedQuestion.category}</span>
            <h2 className="font-bold text-lg">{selectedQuestion.question}</h2>
          </CardContent>
        </Card>

        {/* Video Alanı */}
        <div className="relative bg-black rounded-2xl overflow-hidden mb-4 aspect-video">
          {cameraError ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-800 p-6">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{cameraError.message}</h3>
                {cameraError.instructions && (
                  <div className="bg-gray-700 rounded-lg p-4 mb-4 text-left">
                    {cameraError.instructions.map((instruction, idx) => (
                      <p key={idx} className="text-gray-300 text-sm mb-1">{instruction}</p>
                    ))}
                  </div>
                )}
                <Button 
                  onClick={startCamera}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Tekrar Dene
                </Button>
              </div>
            </div>
          ) : recordedUrl ? (
            <video 
              src={recordedUrl} 
              controls 
              className="w-full h-full object-cover"
            />
          ) : (
            <video 
              ref={videoRef} 
              autoPlay 
              muted 
              playsInline
              className="w-full h-full object-cover"
            />
          )}
          
          {/* Recording Indicator */}
          {isRecording && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span className="text-sm font-medium">{formatTime(recordingTime)}</span>
              <span className="text-xs opacity-80">/ {formatTime(selectedQuestion.timeLimit)}</span>
            </div>
          )}
        </div>

        {/* İpuçları */}
        <Card className="mb-4 border-0 bg-gray-800">
          <CardContent className="p-3">
            <p className="text-amber-400 text-xs font-medium mb-2">💡 İpuçları:</p>
            <div className="flex flex-wrap gap-2">
              {selectedQuestion.tips.map((tip, idx) => (
                <span key={idx} className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded-full">
                  {tip}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Kontroller */}
        <div className="flex justify-center gap-4 mb-4">
          {!recordedUrl ? (
            <>
              {!isRecording ? (
                <Button 
                  onClick={startRecording}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!cameraReady || cameraError}
                >
                  <Play className="w-6 h-6 mr-2" />
                  {!cameraReady && !cameraError ? 'Kamera Başlatılıyor...' : 'Kaydı Başlat'}
                </Button>
              ) : (
                <Button 
                  onClick={stopRecording}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-6 rounded-full"
                >
                  <Square className="w-6 h-6 mr-2" />
                  Kaydı Durdur
                </Button>
              )}
            </>
          ) : (
            <>
              <Button 
                onClick={resetRecording}
                variant="outline"
                className="border-gray-600 text-gray-300 px-6 py-6 rounded-full"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Tekrar Çek
              </Button>
              <Button 
                onClick={analyzeVideo}
                disabled={isAnalyzing || remainingCredits <= 0}
                className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-6 rounded-full"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analiz Ediliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    AI Analiz
                  </>
                )}
              </Button>
              <Button 
                onClick={saveToProfile}
                disabled={isAnalyzing}
                variant="outline"
                className="border-green-600 text-green-400 hover:bg-green-900 px-6 py-6 rounded-full"
              >
                <Save className="w-5 h-5 mr-2" />
                Profile Kaydet
              </Button>
            </>
          )}
        </div>

        {/* Analiz Sonuçları */}
        {analysisResult && (
          <Card className="border-0 bg-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Analiz Sonuçları
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Genel Skor */}
              <div className="text-center p-6 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl">
                <p className="text-white/80 text-sm">Genel Skor</p>
                <p className="text-5xl font-bold text-white">{analysisResult.overall_score}</p>
                <p className="text-white/80 text-sm">/ 100</p>
              </div>

              {/* Detaylı Skorlar */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-700 rounded-xl p-4">
                  <p className="text-gray-400 text-xs">😊 Gülümseme</p>
                  <p className="text-2xl font-bold text-white">{analysisResult.smile_score}/10</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-4">
                  <p className="text-gray-400 text-xs">👀 Göz Teması</p>
                  <p className="text-2xl font-bold text-white">{analysisResult.eye_contact_score}/10</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-4">
                  <p className="text-gray-400 text-xs">🎤 Ses Tonu</p>
                  <p className="text-2xl font-bold text-white">{analysisResult.voice_score}/10</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-4">
                  <p className="text-gray-400 text-xs">📝 İçerik</p>
                  <p className="text-2xl font-bold text-white">{analysisResult.content_score}/10</p>
                </div>
              </div>

              {/* Öneriler */}
              <div className="bg-gray-700 rounded-xl p-4">
                <p className="text-amber-400 text-sm font-medium mb-2">💡 İyileştirme Önerileri</p>
                <ul className="space-y-2">
                  {analysisResult.suggestions?.map((suggestion, idx) => (
                    <li key={idx} className="text-gray-300 text-sm flex items-start gap-2">
                      <TrendingUp className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Transkript */}
              {analysisResult.transcript && (
                <div className="bg-gray-700 rounded-xl p-4">
                  <p className="text-amber-400 text-sm font-medium mb-2">📄 Cevabınız</p>
                  <p className="text-gray-300 text-sm">{analysisResult.transcript}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
